// src/modules/assetManagement/components/AssetThumb.tsx
import React, { useState } from "react";
import { FileAudio, FileImage, FileText, FileVideo, Play } from "lucide-react";
import type { Asset } from "../types";
import { extensionOf } from "../utils";

const KIND_STYLE = {
  image: { Icon: FileImage, className: "bg-violet-50 text-violet-500" },
  video: { Icon: FileVideo, className: "bg-sky-50 text-sky-500" },
  audio: { Icon: FileAudio, className: "bg-pink-50 text-pink-500" },
  document: { Icon: FileText, className: "bg-slate-100 text-slate-500" },
} as const;

/** Thumbnail for grids and tables. Archived assets show no preview (cold storage). */
const AssetThumb: React.FC<{ asset: Asset; size?: "sm" | "md" }> = ({ asset, size = "md" }) => {
  const [failed, setFailed] = useState(false);
  const { Icon, className } = KIND_STYLE[asset.kind];
  const coldStorage = asset.storage !== "standard";
  const canPreview = !!asset.previewUrl && !failed && !coldStorage;
  const iconSize = size === "sm" ? 18 : 28;

  if (canPreview && asset.kind === "image") {
    return (
      <img
        src={asset.previewUrl!}
        alt=""
        loading="lazy"
        className="w-full h-full object-cover"
        onError={() => setFailed(true)}
      />
    );
  }

  if (canPreview && asset.kind === "video") {
    return (
      <div className="relative w-full h-full bg-slate-900">
        <video
          src={`${asset.previewUrl}#t=1`}
          muted
          preload="metadata"
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
        <span className="absolute inset-0 flex items-center justify-center">
          <span className={`rounded-full bg-black/50 text-white flex items-center justify-center ${size === "sm" ? "w-5 h-5" : "w-9 h-9"}`}>
            <Play size={size === "sm" ? 10 : 16} fill="currentColor" />
          </span>
        </span>
      </div>
    );
  }

  // No preview (missing, failed, or archived in cold storage): show the file type.
  // Archived assets look like any other file here; the "Archived" badge marks them.
  return (
    <div className={`relative w-full h-full flex flex-col items-center justify-center gap-1 ${className}`}>
      <Icon size={iconSize} strokeWidth={1.6} />
      {size === "md" && (
        <span className="text-[10px] font-bold tracking-wider uppercase">
          {extensionOf(asset.filename)}
        </span>
      )}
    </div>
  );
};

export default AssetThumb;
